/**
 * useAuthInit Hook
 * Initializes authentication state from storage on app load
 * Restores session if token exists and is valid
 */

import { useEffect } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { setAuth, setUser, setRememberMe, setLoading, setInitialized } from '../../../store/slices/authSlice';
import { getFirebaseAuth } from '../../../config/firebase';
import { mapBackendUserToUser } from '../../../types/user';
import { getErrorDetails, isAxiosErrorWithStatus } from '../../../utils/errorHandling';

/**
 * Initialize auth state from storage
 * Should be called once at app startup
 */
export function useAuthInit() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let initializationHandled = false;

    const markInitialized = () => {
      if (!initializationHandled) {
        dispatch(setInitialized(true));
        dispatch(setLoading(false));
        initializationHandled = true;
      }
    };

    const initializeAuth = async () => {
      if (typeof window === 'undefined') {
        dispatch(setInitialized(true));
        return; // Server-side rendering
      }

      const storedLocalToken = localStorage.getItem('flowrithm_jwt_token');
      const storedSessionToken = sessionStorage.getItem('flowrithm_jwt_token');
      const storedRememberMeFlag = localStorage.getItem('flowrithm_remember_me') === 'true';
      const hasStoredToken = Boolean(storedLocalToken || storedSessionToken);

      if (hasStoredToken) {
        dispatch(setLoading(true));
      }

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          // Firebase not ready - consider initialization complete so routing can proceed
          markInitialized();
          return;
        }

        const { onAuthStateChanged } = await import('firebase/auth');

        unsubscribe = onAuthStateChanged(authInstance, async (firebaseUser) => {
          if (firebaseUser) {
            try {
              const freshToken = await firebaseUser.getIdToken();
              const rememberMe = storedLocalToken ? true : storedRememberMeFlag;

              if (rememberMe) {
                localStorage.setItem('flowrithm_jwt_token', freshToken);
                localStorage.setItem('flowrithm_firebase_uid', firebaseUser.uid);
                localStorage.setItem('flowrithm_remember_me', 'true');
              } else {
                sessionStorage.setItem('flowrithm_jwt_token', freshToken);
                sessionStorage.setItem('flowrithm_firebase_uid', firebaseUser.uid);
                localStorage.removeItem('flowrithm_remember_me');
              }

              dispatch(setRememberMe(rememberMe));
              dispatch(
                setAuth({
                  firebase_uid: firebaseUser.uid,
                  jwt_token: freshToken,
                  rememberMe,
                })
              );

              // Fetch user data from backend
              try {
                const { api } = await import('../../../api/client');
                const userResponse = await api.getCurrentUser();
                if (userResponse.data?.data) {
                  const mappedUser = mapBackendUserToUser(userResponse.data.data);
                  if (mappedUser) {
                    dispatch(setUser(mappedUser));
                  }
                }
              } catch (userError) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch user data on init:', getErrorDetails(userError));
                
                if (isAxiosErrorWithStatus(userError, 500)) {
                  // eslint-disable-next-line no-console
                  console.error('Server error (500) - This indicates a backend issue. Check backend logs for details.');
                }
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.warn('Auth initialization failed while refreshing token:', error);
            } finally {
              markInitialized();
            }
          } else {
            // No authenticated user - clear any stored session
            localStorage.removeItem('flowrithm_jwt_token');
            localStorage.removeItem('flowrithm_firebase_uid');
            sessionStorage.removeItem('flowrithm_jwt_token');
            sessionStorage.removeItem('flowrithm_firebase_uid');
            localStorage.removeItem('flowrithm_remember_me');
            dispatch(setRememberMe(false));
            markInitialized();
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Auth initialization failed:', error);
        localStorage.removeItem('flowrithm_jwt_token');
        localStorage.removeItem('flowrithm_firebase_uid');
        sessionStorage.removeItem('flowrithm_jwt_token');
        sessionStorage.removeItem('flowrithm_firebase_uid');
        localStorage.removeItem('flowrithm_remember_me');
        markInitialized();
      }
    };

    initializeAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dispatch]);
}

